import { isEqual, isFunction } from "lodash";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { AESEncryptURL } from "utils/encryption/AESEncrypt";
import { initialFilter } from "./UserManagementUIHelpers";

// REDUX
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { getListUsers } from "../../redux/user";

// CONTEXT
import useAuth from "../../hooks/useAuth";

const UserManagementUIContext = createContext();

export const useUserManagementUIContext = () => {
  return useContext(UserManagementUIContext);
};

export const UserManagementUIConsumer = UserManagementUIContext.Consumer;

export function UserManagementUIProvider({ children }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isStrictAccess, authData } = useAuth();
  const { getListUsersData } = useSelector(
    (state) => ({
      getListUsersData: state.GetListUsersData,
    }),
    shallowEqual
  );
  const { data, isLoading } = getListUsersData;
  const [queryParams, setQueryParamsBase] = useState(initialFilter);

  useEffect(() => {
    dispatch(getListUsers(queryParams));
  }, [dispatch, queryParams]);

  const setQueryParams = useCallback(
    (nextQueryParams) => {
      dispatch(getListUsers(nextQueryParams));
      setQueryParamsBase((prevQueryParams) => {
        if (isFunction(nextQueryParams)) {
          nextQueryParams = nextQueryParams(prevQueryParams);
        }
        if (isEqual(prevQueryParams, nextQueryParams)) {
          return prevQueryParams;
        }
        return nextQueryParams;
      });
    },
    [dispatch]
  );

  const dataUIEvents = {
    goToCreateUser: () => {
      if (isStrictAccess) {
        navigate(`/user-management/create`, {
          state: {
            showDialog: true,
          },
        });
      } else {
        navigate(-1);
      }
    },
    goToDeleteUser: (data) => {
      const secureID = AESEncryptURL(data.username.toString());
      navigate(`/user-management/${secureID}/delete`, {
        state: {
          showDialog: true,
          userData: data,
        },
      });
    },
    goToUserEdit: (data) => {
      const secureID = AESEncryptURL(data.username.toString());
      navigate(`/user-management/${secureID}/edit`, {
        state: {
          showDialog: true,
          userData: data,
        },
      });
    },
    goToUserChangePass: (data) => {
      const secureID = AESEncryptURL(data.username.toString());
      navigate(`/user-management/${secureID}/changepass`, {
        state: {
          showDialog: true,
          userData: data,
        },
      });
    },
    onHide: () => {
      navigate(-1);
    },
  };

  const value = {
    data,
    authData,
    queryParams,
    getListUsersData,
    isLoadingData: isLoading,
    setQueryParams,
    setQueryParamsBase,
    goToCreateUser: dataUIEvents.goToCreateUser,
    goToDeleteUser: dataUIEvents.goToDeleteUser,
    goToUserEdit: dataUIEvents.goToUserEdit,
    goToUserChangePass: dataUIEvents.goToUserChangePass,
    onHide: dataUIEvents.onHide,
  };

  return (
    <UserManagementUIContext.Provider value={value}>
      {children}
    </UserManagementUIContext.Provider>
  );
}
